import React, { useEffect, useMemo, useRef, useState } from "react";
import config from "../../../config";
import Images from "../../../helpers/images";
import { Image } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { LazyLoadComponent } from "react-lazy-load-image-component";

const MAX_SPREAD_VW = 96;
const MAX_PAGE_VH = 95;
const FALLBACK_ASPECT = 30 / 42;

const LENS_SIZE = 150;
const ZOOM = 1.8;

const GlassLens = ({ src }) => {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [active, setActive] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const handleMove = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
      setActive(false);
      return;
    }
    setPos({ x, y });
    setActive(true);
  };

  const width = wrapRef.current?.clientWidth || 0;
  const height = wrapRef.current?.clientHeight || 0;

  return (
    <div
      ref={wrapRef}
      onMouseMove={handleMove}
      onMouseEnter={handleMove}
      onMouseLeave={() => setActive(false)}
      style={{ position: "relative", width: "100%", height: "100%" }}
    >
      <img
        ref={imgRef}
        src={src}
        alt=""
        draggable={false}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          objectFit: "contain",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          pointerEvents: "none",
          width: LENS_SIZE,
          height: LENS_SIZE,
          borderRadius: "50%",
          border: "1px solid #000",
          boxShadow: "0 0 8px rgba(0,0,0,0.3)",
          left: pos.x - LENS_SIZE / 2,
          top: pos.y - LENS_SIZE / 2,
          backgroundImage: `url(${src})`,
          backgroundRepeat: "no-repeat",
          backgroundColor: "#fff",
          backgroundSize: `${width * ZOOM}px ${height * ZOOM}px`,
          backgroundPosition: `-${pos.x * ZOOM - LENS_SIZE / 2}px -${
            pos.y * ZOOM - LENS_SIZE / 2
          }px`,
          opacity: active ? 1 : 0,
          transition: "opacity 0.15s ease",
          zIndex: 10,
        }}
      />
    </div>
  );
};

const Book = ({ selectedDigitalShade, handleClose }) => {
  const originalImages = useMemo(
    () => [...(selectedDigitalShade?.images || [])],
    [selectedDigitalShade]
  );
  const [flipped, setFlipped] = useState(() => new Set());
  const [pageAspect, setPageAspect] = useState(FALLBACK_ASPECT);

  useEffect(() => {
    const first = originalImages?.[0];
    if (!first) return;
    const img = new window.Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setPageAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = `${config.DIGITAL_SHDAE_CARD_IMG_URL}/${first.filename}`;
  }, [originalImages]);

  const bookStyle = useMemo(() => {
    const pageWidthFromVw = MAX_SPREAD_VW / 2;
    const pageWidthFromVh = `calc(${MAX_PAGE_VH}vh * ${pageAspect})`;
    const pageWidth = `min(${pageWidthFromVw}vw, ${pageWidthFromVh})`;
    const pageHeight = `calc(${pageWidth} / ${pageAspect})`;
    return {
      pages: {
        width: `calc(${pageWidth} * 2)`,
        height: pageHeight,
      },
      page: {
        width: pageWidth,
        height: pageHeight,
      },
    };
  }, [pageAspect]);

  const handlePageClick = (index) => {
    setFlipped((prev) => {
      const next = new Set(prev);
      const isRightFacing = index % 2 === 0;
      if (isRightFacing) {
        if (index + 1 < originalImages.length) {
          next.add(index);
          next.add(index + 1);
        }
      } else {
        next.delete(index);
        next.delete(index - 1);
      }
      return next;
    });
  };

  return (
    <>
      <div className="book">
        <button
          type="button"
          onClick={handleClose}
          className="book-close-btn"
          aria-label="Close"
        >
          <Image src={Images.Close} />
        </button>
        <div id="pages" className="pages auto-size" style={bookStyle.pages}>
          {originalImages.map((item, i) => (
            <div
              className={`page${flipped.has(i) ? " flipped" : ""}`}
              key={`pages${i}`}
              onClick={() => handlePageClick(i)}
              style={{
                ...bookStyle.page,
                zIndex:
                  i % 2 === 0
                    ? originalImages.length - i
                    : originalImages.length - i,
              }}
            >
              <LazyLoadComponent>
                {isMobile ? (
                  <Image
                    loading="lazy"
                    src={`${config.DIGITAL_SHDAE_CARD_IMG_URL}/${item.filename}`}
                    className="img-fluid img1"
                    alt="Your Image"
                  />
                ) : (
                  <GlassLens
                    src={`${config.DIGITAL_SHDAE_CARD_IMG_URL}/${item.filename}`}
                  />
                )}
              </LazyLoadComponent>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default Book;
